import React, { useState, useEffect } from 'react';
import { API_URL } from '../config/constants';

interface Category {
  category: string;
  count: number;
}

// Date fictive pentru filtre
const languageOptions = ['Română', 'Engleză', 'Franceză', 'Germană'];
const yearOptions = {
  min: 1800,
  max: 2023
};

interface AdvancedSearchProps {
  onApplyFilters: (filters: any) => void;
  onClose: () => void;
  initialCategory?: string;
}

const AdvancedSearch: React.FC<AdvancedSearchProps> = ({ onApplyFilters, onClose, initialCategory = '' }) => {
  const [title, setTitle] = useState('');
  const [author, setAuthor] = useState('');
  const [genre, setGenre] = useState(initialCategory);
  const [language, setLanguage] = useState('');
  const [yearMin, setYearMin] = useState<number | ''>('');
  const [yearMax, setYearMax] = useState<number | ''>('');
  const [isbn, setIsbn] = useState('');
  const [publisher, setPublisher] = useState('');
  const [categories, setCategories] = useState<Category[]>([]);

  // Încărcăm categoriile din backend
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${API_URL}/books/categories`);
        if (response.ok) {
          const data = await response.json();
          setCategories(data);
        }
      } catch (error) {
        console.error('Eroare la încărcarea categoriilor:', error);
        // Fallback la categoriile predefinite
        const genreOptions = [
          'Ficțiune', 'Non-Ficțiune', 'Fantasy', 'Distopie', 'Clasic', 
          'Memorialistică', 'Mister', 'Science Fiction', 'Științifice',
          'Academice', 'Biografii', 'Poezie'
        ];
        const fallbackCategories = genreOptions.map(category => ({
          category,
          count: 0
        }));
        setCategories(fallbackCategories);
      }
    };
    fetchCategories();
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const filters = {
      title: title.trim(),
      author: author.trim(),
      genre: genre,
      language: language,
      year: {
        min: yearMin === '' ? yearOptions.min : yearMin,
        max: yearMax === '' ? yearOptions.max : yearMax
      },
      isbn: isbn.trim(),
      publisher: publisher.trim()
    };
    
    onApplyFilters(filters);
  };

  const handleReset = () => {
    setTitle('');
    setAuthor('');
    setGenre('');
    setLanguage('');
    setYearMin('');
    setYearMax('');
    setIsbn('');
    setPublisher('');
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="flex justify-between items-center px-6 py-4 bg-gray-50 border-b">
        <h3 className="text-lg font-semibold text-gray-800">Căutare avansată</h3>
        <button 
          onClick={onClose}
          className="text-gray-500 hover:text-gray-700"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      
      <form onSubmit={handleSubmit} className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Titlu */}
          <div className="space-y-2">
            <label htmlFor="title" className="block text-sm font-medium text-gray-700">
              Titlu
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="Caută după titlu"
            />
          </div>
          
          {/* Autor */}
          <div className="space-y-2">
            <label htmlFor="author" className="block text-sm font-medium text-gray-700">
              Autor
            </label>
            <input
              type="text"
              id="author"
              name="author"
              value={author}
              onChange={(e) => setAuthor(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="Caută după autor"
            />
          </div>
          
          {/* Gen/Categorie */}
          <div className="space-y-2">
            <label htmlFor="genre" className="block text-sm font-medium text-gray-700">
              Categorie
            </label>
            <select
              id="genre"
              name="genre"
              value={genre}
              onChange={(e) => setGenre(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Toate categoriile</option>
              {categories.map(({ category, count }) => (
                <option key={category} value={category}>
                  {category} ({count} cărți)
                </option>
              ))}
            </select>
          </div>
          
          {/* Limba */}
          <div className="space-y-2">
            <label htmlFor="language" className="block text-sm font-medium text-gray-700">
              Limba
            </label>
            <select
              id="language"
              name="language"
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Toate limbile</option>
              {languageOptions.map((l) => (
                <option key={l} value={l}>
                  {l}
                </option>
              ))}
            </select>
          </div>
          
          {/* Interval ani */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Anul publicării
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="number"
                name="yearMin"
                value={yearMin}
                onChange={(e) => setYearMin(e.target.value ? parseInt(e.target.value) : '')}
                min={yearOptions.min}
                max={yearOptions.max}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="De la"
              />
              <span className="text-gray-500">-</span>
              <input
                type="number"
                name="yearMax"
                value={yearMax}
                onChange={(e) => setYearMax(e.target.value ? parseInt(e.target.value) : '')}
                min={yearOptions.min}
                max={yearOptions.max}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Până la"
              />
            </div>
          </div>
          
          {/* ISBN */}
          <div className="space-y-2">
            <label htmlFor="isbn" className="block text-sm font-medium text-gray-700">
              ISBN
            </label>
            <input
              type="text"
              id="isbn"
              name="isbn"
              value={isbn}
              onChange={(e) => setIsbn(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="Cod ISBN"
            />
          </div>
          
          {/* Editura */}
          <div className="space-y-2 md:col-span-2 lg:col-span-1">
            <label htmlFor="publisher" className="block text-sm font-medium text-gray-700">
              Editura
            </label>
            <input
              type="text"
              id="publisher"
              name="publisher"
              value={publisher}
              onChange={(e) => setPublisher(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="Caută după editură"
            />
          </div>
        </div>
        
        <div className="mt-8 flex justify-end space-x-4">
          <button
            type="button"
            onClick={handleReset}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Resetează
          </button>
          <button
            type="submit"
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Aplică filtrele
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdvancedSearch; 